import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip
} from '@material-ui/core';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useBooking } from '../../contexts/BookingContext';
import { useTrip } from '../../contexts/TripContext';
import { useCompany } from '../../contexts/CompanyContext';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Status color mapping
const STATUS_COLORS = {
  confirmed: 'success',
  pending: 'warning',
  cancelled: 'error',
  completed: 'primary',
  refunded: 'secondary'
};

const BookingsDashboard = () => {
  const { getBookings, getBookingStats } = useBooking();
  const { getTrips } = useTrip();
  const { getCompanies } = useCompany();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [companyFilter, setCompanyFilter] = useState('all');
  
  // Dashboard data
  const [bookings, setBookings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({
    totalBookings: 0,
    confirmedBookings: 0,
    pendingBookings: 0,
    cancelledBookings: 0,
    totalRevenue: 0,
    averageBookingValue: 0
  });
  
  // Chart data
  const [bookingsByStatusData, setBookingsByStatusData] = useState([]);
  const [bookingsByRouteData, setBookingsByRouteData] = useState([]);
  const [bookingsTrendData, setBookingsTrendData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    const fetchBookingsDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all required data
        const allBookings = await getBookings();
        const allTrips = await getTrips();
        const allCompanies = await getCompanies();
        
        setCompanies(allCompanies);
        setTrips(allTrips);
        
        // Filter bookings based on company filter
        let filteredBookings = allBookings;
        if (companyFilter !== 'all') {
          // First get all trips for the selected company
          const companyTrips = allTrips.filter(trip => trip.companyId === companyFilter);
          const companyTripIds = companyTrips.map(trip => trip.id);
          
          // Then filter bookings for those trips
          filteredBookings = allBookings.filter(booking => 
            companyTripIds.includes(booking.tripId)
          );
        }
        
        // Filter bookings based on time range
        const now = new Date();
        let startDate;
        
        switch (timeRange) {
          case 'week':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate = new Date(now);
            startDate.setFullYear(now.getFullYear() - 1);
            break;
          default:
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
        }
        
        filteredBookings = filteredBookings.filter(booking => {
          const bookingDate = new Date(booking.createdAt.seconds * 1000);
          return bookingDate >= startDate;
        });
        
        setBookings(filteredBookings);
        
        // Calculate basic stats
        const totalBookings = filteredBookings.length;
        const confirmedBookings = filteredBookings.filter(booking => booking.status === 'confirmed').length;
        const pendingBookings = filteredBookings.filter(booking => booking.status === 'pending').length;
        const cancelledBookings = filteredBookings.filter(booking => booking.status === 'cancelled').length;
        
        // Calculate total revenue
        let totalRevenue = 0;
        filteredBookings.forEach(booking => {
          if (booking.status !== 'cancelled' && booking.paymentStatus === 'completed') {
            totalRevenue += booking.amount || 0;
          }
        });
        
        // Calculate average booking value
        const completedBookings = filteredBookings.filter(
          booking => booking.status !== 'cancelled' && booking.paymentStatus === 'completed'
        );
        const averageBookingValue = completedBookings.length > 0 
          ? totalRevenue / completedBookings.length 
          : 0;
        
        setStats({
          totalBookings,
          confirmedBookings,
          pendingBookings,
          cancelledBookings,
          totalRevenue,
          averageBookingValue
        });
        
        // Prepare bookings by status data for chart
        const bookingsByStatus = [
          { name: 'Confirmed', value: confirmedBookings },
          { name: 'Pending', value: pendingBookings },
          { name: 'Cancelled', value: cancelledBookings },
          { name: 'Completed', value: filteredBookings.filter(booking => booking.status === 'completed').length },
          { name: 'Refunded', value: filteredBookings.filter(booking => booking.status === 'refunded').length }
        ];
        setBookingsByStatusData(bookingsByStatus);
        
        // Prepare bookings by route data
        const routeCounts = {};
        
        filteredBookings.forEach(booking => {
          const trip = allTrips.find(t => t.id === booking.tripId);
          if (trip) {
            const route = `${trip.origin} - ${trip.destination}`;
            if (!routeCounts[route]) {
              routeCounts[route] = 0;
            }
            routeCounts[route]++;
          }
        });
        
        const bookingsByRoute = Object.keys(routeCounts).map(route => ({
          name: route,
          value: routeCounts[route]
        })).sort((a, b) => b.value - a.value).slice(0, 10); // Top 10 routes
        
        setBookingsByRouteData(bookingsByRoute);
        
        // Prepare bookings trend data
        const bookingsByDate = {};
        
        // Initialize all dates in the range
        let currentDate = new Date(startDate);
        while (currentDate <= now) {
          let dateKey;
          
          if (timeRange === 'week') {
            // Format as day of week (e.g., "Mon")
            dateKey = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
          } else if (timeRange === 'month') {
            // Format as day of month (e.g., "15")
            dateKey = currentDate.getDate().toString();
          } else {
            // Format as month (e.g., "Jan")
            dateKey = currentDate.toLocaleDateString('en-US', { month: 'short' });
          }
          
          if (!bookingsByDate[dateKey]) {
            bookingsByDate[dateKey] = 0;
          }
          
          // Increment date based on time range
          if (timeRange === 'week' || timeRange === 'month') {
            currentDate.setDate(currentDate.getDate() + 1);
          } else {
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
        }
        
        // Count bookings by date
        filteredBookings.forEach(booking => {
          const bookingDate = new Date(booking.createdAt.seconds * 1000);
          let dateKey;
          
          if (timeRange === 'week') {
            dateKey = bookingDate.toLocaleDateString('en-US', { weekday: 'short' });
          } else if (timeRange === 'month') {
            dateKey = bookingDate.getDate().toString();
          } else {
            dateKey = bookingDate.toLocaleDateString('en-US', { month: 'short' });
          }
          
          if (bookingsByDate[dateKey] !== undefined) {
            bookingsByDate[dateKey]++;
          }
        });
        
        // Convert to array format for chart
        const bookingsTrend = Object.keys(bookingsByDate).map(date => ({
          date,
          bookings: bookingsByDate[date]
        }));
        
        setBookingsTrendData(bookingsTrend);
        
        // Prepare payment method data
        const paymentMethods = {};
        
        filteredBookings.forEach(booking => {
          if (booking.paymentMethod) {
            if (!paymentMethods[booking.paymentMethod]) {
              paymentMethods[booking.paymentMethod] = 0;
            }
            paymentMethods[booking.paymentMethod]++;
          }
        });
        
        const paymentMethodArray = Object.keys(paymentMethods).map(method => ({
          name: method.charAt(0).toUpperCase() + method.slice(1), // Capitalize first letter
          value: paymentMethods[method]
        }));
        
        setPaymentMethodData(paymentMethodArray);
        
      } catch (error) {
        console.error('Error fetching bookings dashboard data:', error);
        setError('Failed to load bookings dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookingsDashboardData();
  }, [timeRange, companyFilter]);
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  const handleCompanyFilterChange = (event) => {
    setCompanyFilter(event.target.value);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Helper function to get trip details
  const getTripDetails = (tripId) => {
    const trip = trips.find(t => t.id === tripId);
    return trip ? `${trip.origin} - ${trip.destination}` : 'Unknown';
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Bookings Dashboard
      </Typography>
      
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={handleTimeRangeChange}
            label="Time Range"
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Company</InputLabel>
          <Select
            value={companyFilter}
            onChange={handleCompanyFilterChange}
            label="Company"
          >
            <MenuItem value="all">All Companies</MenuItem>
            {companies.map(company => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Bookings
              </Typography>
              <Typography variant="h4">
                {stats.totalBookings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Confirmed
              </Typography>
              <Typography variant="h4">
                {stats.confirmedBookings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4">
                {stats.pendingBookings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cancelled
              </Typography>
              <Typography variant="h4">
                {stats.cancelledBookings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4">
                ${stats.totalRevenue.toFixed(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg. Booking
              </Typography>
              <Typography variant="h4">
                ${stats.averageBookingValue.toFixed(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3}>
        {/* Bookings Trend Chart */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Booking Trends
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={bookingsTrendData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="bookings" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                  name="Bookings"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Bookings by Status Chart */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Bookings by Status
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={bookingsByStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {bookingsByStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Bookings by Route Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Top Routes by Booking Count
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={bookingsByRouteData}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 30,
                  left: 100,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name="Bookings" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Payment Methods Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Payment Methods
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentMethodData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {paymentMethodData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Bookings Table */}
      <Paper sx={{ width: '100%', mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Recent Bookings
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>{booking.id.substring(0, 8)}</TableCell>
                    <TableCell>{booking.userId.substring(0, 8)}</TableCell>
                    <TableCell>{getTripDetails(booking.tripId)}</TableCell>
                    <TableCell>
                      {booking.createdAt 
                        ? new Date(booking.createdAt.seconds * 1000).toLocaleString() 
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={booking.status} 
                        color={STATUS_COLORS[booking.status] || 'default'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={booking.paymentStatus || 'N/A'} 
                        color={booking.paymentStatus === 'completed' ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>${booking.amount || 0}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={bookings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default BookingsDashboard;
